import { useCallback, useId } from 'react';

import type { ButtonProps } from '@farmersdog/corgi-x';
import {
  Button,
  Callout,
  Checkbox,
  Flex,
  Nowrap,
  Text,
} from '@farmersdog/corgi-x';
import { Information } from '@farmersdog/corgi-x/icons';

import { trackClicked } from '../../../analytics/events/trackClicked';

import styles from './SubmitButton.css';

const BUTTON_PROPS = {
  type: 'link',
  target: '_blank',
  rel: 'noopener noreferrer',
  variant: 'plain-text',
  color: 'kale-dark',
  weight: 'normal',
  size: 'sm',
  underline: true,
} as const satisfies Partial<ButtonProps<'link', 'plain-text'>>;

const CLICKED_TRACK_PROPS = {
  module_name: 'terms_checkbox',
} as const;

const BUTTON_CLICKED_TRACK_PROPS = {
  ...CLICKED_TRACK_PROPS,
  component: 'text',
} as const;

const trackClickedTermsOfUse = () => {
  trackClicked({
    ...BUTTON_CLICKED_TRACK_PROPS,
    text: 'terms of use',
  });
};

const trackClickedTermsPrivacyPolicy = () => {
  trackClicked({
    ...BUTTON_CLICKED_TRACK_PROPS,
    text: 'privacy policy',
  });
};

interface TermsAndConditionsCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  shouldShowError: boolean;
  petName?: string;
}

export function TermsAndConditionsCheckbox({
  checked,
  onChange,
  shouldShowError,
  petName,
}: TermsAndConditionsCheckboxProps) {
  const calloutId = useId();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.checked);

      trackClicked({
        ...CLICKED_TRACK_PROPS,
        component: 'checkbox',
        checked: e.target.checked,
      });
    },
    [onChange]
  );

  return (
    <Flex direction="column" gap="sm" alignItems="center">
      {shouldShowError && (
        <Callout
          variant="neutral"
          id={calloutId}
          icon={
            <Information
              size={13}
              className={styles.termsAndConditionsWarningIcon}
            />
          }
          className={styles.termsAndConditionsWarning}
        >
          <Text variant="heading-12">
            Please check the box to get {petName ?? 'your dogs'} started with
            fresh human-grade food!
          </Text>
        </Callout>
      )}

      <Checkbox
        className={styles.termsAndConditionsCheckbox}
        label={
          <Text variant="heading-12">
            I agree to the{' '}
            <Button
              {...BUTTON_PROPS}
              to="/content/terms"
              onClick={trackClickedTermsOfUse}
            >
              Terms of Use
            </Button>{' '}
            and have read the{' '}
            <Nowrap>
              <Button
                {...BUTTON_PROPS}
                to="/content/privacy"
                onClick={trackClickedTermsPrivacyPolicy}
              >
                Privacy Policy
              </Button>
              .
            </Nowrap>
          </Text>
        }
        aria-describedby={calloutId}
        invalid={shouldShowError}
        onChange={handleChange}
        checked={checked}
      />
    </Flex>
  );
}
